import PNLBg from '@/assets/feed/PNL.png';
import { useIsUserVerified } from '@/hooks/useIsUserVerified';
import { usePairs } from '@/store/pairs';
import { usePrice } from '@/store/price';
import { useAddress } from '@/store/selector';
import { formatAddress } from '@/utils/format-address';
import { formatDisplay } from '@/utils/format-display';
import { formatTime } from '@/utils/use-test';
import { MiniKit } from '@worldcoin/minikit-js';
import BigNumber from 'bignumber.js';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { isEqual } from 'lodash';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ListRange, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useShallow } from 'zustand/shallow';
import { useBalance } from '../future/data';
import { buysell } from '../future/func';
import { OrderType, Type } from '../future/type';
import SpinLoading from '../loading/SpinLoading';
import { toastApp } from '../toast-message/sonner';
import RowLoading from './LoadingFeed';
import { useFeedStore } from './useFeedStore';

dayjs.extend(utc);
dayjs.extend(relativeTime);
// =============================================
// 1. TYPES
// =============================================
export interface FeedItem {
    isMarket: boolean;
    trader: string;
    buy: boolean;
    pair: number;
    openPrice: number;
    positionSize: number;
    leverage: number;
    createdAt: string;
}

// =============================================
// 2. COMPONENT ROW
// =============================================

const TrendUpIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
        <polyline points="17 6 23 6 23 12"></polyline>
    </svg>
);

const DownIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="10"
        viewBox="0 0 17 10"
        fill="none"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.6035 9.33355C16.6452 9.23188 16.6668 9.12522 16.6668 9.01688L16.6661 4.92522C16.6661 4.46438 16.2919 4.09188 15.8328 4.09188C15.3719 4.09188 14.9994 4.46522 14.9994 4.92522V7.00522L9.94442 1.95271C9.61942 1.62688 9.09183 1.62688 8.766 1.95271L5.94688 4.77188L1.42271 0.244375C1.09688 -0.0814583 0.569375 -0.0814583 0.244375 0.244375C-0.0814583 0.568542 -0.0814583 1.09687 0.244375 1.42271L5.35771 6.53938C5.51438 6.69605 5.72604 6.78355 5.94688 6.78355C6.16854 6.78355 6.38017 6.69605 6.536 6.53938L9.35517 3.72022L13.8211 8.18355H11.7419C11.2811 8.18355 10.9086 8.55605 10.9086 9.01688C10.9086 9.47772 11.2811 9.85022 11.7419 9.85022H15.8335C15.941 9.85022 16.0485 9.82855 16.1493 9.78772C16.251 9.74605 16.3436 9.68522 16.4228 9.60605C16.5011 9.52772 16.5619 9.43438 16.6035 9.33355Z"
            fill="#FF695F"
        />
    </svg>
);

const InfoTrader = (props: { item: FeedItem }) => {
    const { item } = props;

    const { data: verified } = useIsUserVerified(item.trader);
    const { i18n } = useTranslation();
    return (
        <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => {
                MiniKit.showProfileCard(undefined, item.trader);
            }}
        >
            {/* Avatar Placeholder - Thay bằng component Avatar của bạn nếu có */}
            <div className="w-10 h-10 rounded-full bg-[#2a2a2a] overflow-hidden border border-[#333]">
                <img
                    src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${item.trader}`}
                    alt="avatar"
                    className="w-full h-full object-cover"
                />
            </div>

            <div className="flex flex-col">
                <div className="flex items-center gap-1">
                    <div className="font-medium text-[15px] text-white flex-1">
                        {formatAddress(item.trader)}
                    </div>
                    <img
                        src={
                            verified
                                ? '/images/verified.png'
                                : '/images/un_verified.png'
                        }
                        className="w-4 h-4"
                    />
                </div>
                <span className="text-xs text-[#808080] mt-0.5">
                    {formatTime(item.createdAt, i18n.language)}
                </span>
            </div>
        </div>
    );
};

const Row: React.FC<{ item: FeedItem }> = ({ item }) => {
    const isLong = item.buy;
    const pairs = usePairs(useShallow((state) => state.data));
    const { t } = useTranslation();
    const address = useAddress();
    const { data: usdcBalance } = useBalance();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState('');

    const data = usePrice(useShallow((state) => state.data));
    const currentPrice = Number(data[item.pair]?.price ?? 0);

    // Tính toán Position Size
    const { pair, amount, marginAmount, openPriceReal } = useMemo(() => {
        // 1. Tìm Pair
        const foundPair = pairs.find((p) => p.pairIndex === Number(item.pair));

        // 2. Tính Position Size & Margin
        const posSizeBig = new BigNumber(
            Math.ceil(
                new BigNumber(item.positionSize).dividedBy(1e18).toNumber()
            )
        ).multipliedBy(1e18);

        const a = posSizeBig.toNumber();
        const margin = a / 1e18;

        // 3. Open Price thực
        const openPrice = item.openPrice / 1e10;

        return {
            pair: foundPair,
            amount: a,
            marginAmount: margin,
            openPriceReal: openPrice,
        };
    }, [item.pair, item.positionSize, item.openPrice, pairs]);

    // --- USEMEMO 2: TÍNH TOÁN PNL (Chạy khi giá thay đổi) ---
    const {
        calculatedPnlPercent,
        calculatedPnlValue,
        isProfit,
        pnlColorClass,
    } = useMemo(() => {
        // Giá Mark (nếu chưa có giá current thì lấy giá open)
        const markPrice = currentPrice > 0 ? currentPrice : openPriceReal;

        let pnlRate = 0;
        if (openPriceReal > 0) {
            if (isLong) {
                pnlRate = (markPrice - openPriceReal) / openPriceReal;
            } else {
                pnlRate = (openPriceReal - markPrice) / openPriceReal;
            }
        }

        const pnlPercent = pnlRate * item.leverage * 100;

        const pnlValue = marginAmount * pnlRate * item.leverage;

        const profit = pnlPercent >= 0;

        return {
            calculatedPnlPercent: pnlPercent,
            calculatedPnlValue: pnlValue,
            isProfit: profit,
            pnlColorClass: profit ? 'text-[#00ff41]' : 'text-[#FF695F]',
        };
    }, [currentPrice, openPriceReal, isLong, item.leverage, marginAmount]);
    // Logic xử lý Copy/Counter (Giữ nguyên logic của bạn)
    const handleReopenOrder = async (isBuy: boolean, typeLoading: string) => {
        if (new BigNumber(usdcBalance?.toString() ?? '0').lt(amount)) {
            return toastApp.error(t('Not enough balance'));
        }
        // positionSize = item.leverage * amount (amount 1e18)
        const positionSize = new BigNumber(item.leverage).multipliedBy(amount);

        if (
            typeof pair?.maxPosition == 'number' &&
            positionSize.gt(new BigNumber(pair.maxPosition).multipliedBy(1e18))
        ) {
            return toastApp.error(`Max position size is ${pair.maxPosition}`);
        }

        setIsLoading(typeLoading);
        await buysell({
            address,
            buy: isBuy,
            onSuccess: () => {
                navigate('/orders?tab=Position', {
                    state: { isOpened: true },
                });
            },
        })({
            pairIndex: item.pair.toString(),
            leverage: item.leverage,
            amount: new BigNumber(amount).dividedBy(1e18).toString(),
            orderType: OrderType.Market,
            tp: '',
            sl: '',
            type: isBuy ? Type.Long : Type.Short,
            limitPrice: '',
        });
        setIsLoading('');
    };

    // --- MOCK DATA CHO UI (Bạn cần thay bằng data thật từ item) ---
    // Ví dụ: const pnlPercent = item.pnlPercentage || 41;
    // const pnlValue = item.pnl || 547;

    // Màu sắc chủ đạo

    if (calculatedPnlPercent < -80) {
        return null;
    }

    return (
        <div className="p-4 bg-[#141414] text-white rounded-3xl my-4 ">
            {/* --- HEADER --- */}
            <div className="flex justify-between items-start">
                {/* Left: Avatar + Info */}
                <InfoTrader item={item} />

                {/* Right: Asset + Badge */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 font-medium text-sm">
                        {/* Icon Coin */}
                        {pair?.imageUrl && (
                            <img
                                src={pair.imageUrl}
                                className="w-5 h-5 "
                                alt=""
                            />
                        )}
                        <span>{pair?.symbol.split('USD')[0]}</span>
                    </div>

                    {/* Badge Long/Short + Leverage */}
                    <div
                        className={clsx(
                            'px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wide',
                            {
                                'text-[#38F24A] bg-[rgba(56,242,74,0.10)] border  border-[rgba(56,242,74,0.10)]':
                                    isLong,
                                'text-[#FF695F] bg-[rgba(255,105,95,0.10)]  border border-[rgba(255,105,95,0.10)]':
                                    !isLong,
                            }
                        )}
                    >
                        {isLong ? 'LONG' : 'SHORT'} {item.leverage}x
                    </div>
                </div>
            </div>

            {/* --- PNL BANNER (Mới) --- */}
            <div
                className={clsx(
                    'mt-4 py-4 px-[14px] rounded-xl flex items-center justify-between relative overflow-hidden',
                    isProfit
                        ? ' bg-cover bg-center'
                        : 'bg-gradient-to-r from-transparent to-[rgba(255,105,95,0.05)] border border-transparent'
                )}
                style={isProfit ? { backgroundImage: `url(${PNLBg})` } : {}}
            >
                <div
                    className={clsx(
                        'absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none',
                        isProfit
                            ? 'bg-green-500/10 blur-xl'
                            : 'bg-red-500/10 blur-xl'
                    )}
                ></div>

                <div
                    className={clsx(
                        'flex items-center gap-2 font-medium text-base relative z-10',
                        pnlColorClass
                    )}
                >
                    {isProfit ? <TrendUpIcon /> : <DownIcon />}
                    <span>
                        PNL: {calculatedPnlPercent > 0 ? '+' : ''}
                        {calculatedPnlPercent.toFixed(2)}%
                    </span>
                </div>

                <div
                    className={clsx(
                        'font-medium text-base relative z-10',
                        pnlColorClass
                    )}
                >
                    {calculatedPnlValue > 0 ? '+' : ''}
                    {calculatedPnlValue.toFixed(2)} WLD
                </div>
            </div>

            {/* --- STATS GRID --- */}
            <div className="mt-4 grid grid-cols-4 gap-2 text-sm">
                {/* Column 1: Pair */}

                <div className="text-left sm:text-right">
                    <div className="text-[#A3A8B5] text-[11px] mb-1">
                        {t('Amount')}
                    </div>
                    <div className="font-medium text-white">
                        {formatDisplay(amount / 1e18, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </div>
                </div>

                {/* Column 2: Leverage */}
                <div>
                    <div className="text-[#A3A8B5] text-[11px] mb-1">
                        {t('Leverage')}
                    </div>
                    <div className="font-medium text-white">
                        {item.leverage}x
                    </div>
                </div>

                {/* Column 3: Position */}
                <div>
                    <div className="text-[#A3A8B5] text-[11px] text-right mb-1">
                        {t('Position')}
                    </div>
                    <div className="font-medium text-white text-right">
                        {formatDisplay((amount / 1e18) * item.leverage, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </div>
                </div>

                {/* Column 4: Open Price */}
                <div className="text-right">
                    <div className="text-[#A3A8B5] text-[11px] mb-1">
                        {t('Open Price')}
                    </div>
                    <div className="font-medium text-white">
                        {formatDisplay(item.openPrice / 1e10, {
                            decimalToShow: 2,
                        })}
                    </div>
                </div>
            </div>

            {/* --- ACTIONS --- */}
            <div className="flex items-center gap-3 mt-5">
                {/* Nút Counter (Nhỏ hơn, tối màu) */}
                <button
                    type="button"
                    disabled={!!isLoading}
                    className="bg-[#262626] hover:bg-[#333] text-white h-10 px-6 rounded-xl text-sm font-medium transition-colors flex items-center justify-center min-w-[100px]"
                    onClick={() => handleReopenOrder(!item.buy, 'Counter')}
                >
                    {isLoading === 'Counter' ? (
                        <SpinLoading className="!w-4 !h-4 mx-auto" />
                    ) : (
                        t('Counter')
                    )}
                </button>

                {/* Nút Copy Trade (Lớn hơn, màu trắng nổi bật) */}
                <button
                    type="button"
                    disabled={!!isLoading}
                    className="disabled:opacity-50 bg-white hover:bg-gray-100 text-black flex-1 h-10 rounded-xl text-sm font-medium transition-colors flex items-center justify-center shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                    onClick={() => handleReopenOrder(item.buy, 'Copy')}
                >
                    {isLoading === 'Copy' ? (
                        <SpinLoading className="!w-4 !h-4 mx-auto border-black border-b-transparent" />
                    ) : (
                        t('Copy Trade')
                    )}
                </button>
            </div>
        </div>
    );
};

const RowView = memo(Row, isEqual);

// =============================================
// 3. MAIN COMPONENT
// =============================================

const FeedList: React.FC = () => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const { t } = useTranslation();

    const { feed, pending, showNewButton, mergePending, setAtTop } =
        useFeedStore((state) => ({
            feed: state.feed,
            pending: state.pending,
            showNewButton: state.showNewButton,
            mergePending: state.mergePending,
            setAtTop: state.setAtTop,
        }));

    const handleRangeChanged = (range: ListRange) => {
        setAtTop(range.startIndex === 0);
    };

    const handleShowNew = () => {
        mergePending();
        virtuosoRef.current?.scrollToIndex({ index: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        mergePending();
    }, [mergePending]);

    return (
        <div className="mx-auto  h-full rounded-xl shadow-lg overflow-hidden relative">
            {/* Header */}
            {/* <h2 className="text-white text-lg font-medium p-4 ">{t('Feed')}</h2> */}

            {/* Nút hiển thị giao dịch mới */}
            {showNewButton && (
                <button
                    type="button"
                    onClick={handleShowNew}
                    className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-blue-600 text-white text-sm px-4 py-1 rounded-full shadow-md hover:bg-blue-500 transition"
                >
                    {t('View')} {pending.length} {t('new trades')}
                </button>
            )}

            {/* Virtuoso List */}

            {feed.length && (
                <Virtuoso
                    ref={virtuosoRef}
                    className="h-full no-scrollbar"
                    data={feed}
                    itemContent={(index, item) => <RowView item={item} />}
                    overscan={1000}
                    increaseViewportBy={{ top: 200, bottom: 200 }}
                    rangeChanged={handleRangeChanged}
                />
            )}

            {!feed.length && [1, 2, 3].map((item) => <RowLoading key={item} />)}
        </div>
    );
};

export default FeedList;
